/**
 * AdSense ad initialization helper
 *
 * Uses IntersectionObserver to initialize AdSense ads only when their containers
 * become visible. This handles:
 * - Responsive layouts (hidden sm:block, hidden lg:block, etc.)
 * - Viewport resizing (mobile to desktop transitions)
 * - Lazy loading for better performance
 * - Prevents "No slot size for availableWidth=0" errors
 */

(function() {
    'use strict';

    function isAlreadyInitialized(adElement) {
        // data-adsbygoogle-status is set internally by adsbygoogle.js when it
        // processes a slot. It is undocumented but stable, and lets us avoid
        // re-pushing slots restored from bfcache or otherwise reused.
        return adElement.dataset.adsenseInitialized === 'true' ||
            adElement.getAttribute('data-adsbygoogle-status') === 'done';
    }

    function isQueued(adElement) {
        return adElement.dataset.adsenseQueued === 'true';
    }

    // adsbygoogle.push({}) is a page-global request: it fills the next
    // <ins class="adsbygoogle"> that AdSense has not processed yet, not a specific
    // element. When every slot on the page already carries data-adsbygoogle-status,
    // a push has nothing to fill and AdSense throws "All 'ins' elements in the DOM
    // with class=adsbygoogle already have ads in them." Returns false in that case
    // so callers can skip the push.
    function hasUnfilledAdSlot() {
        var slots = document.querySelectorAll('ins.adsbygoogle');
        for (var i = 0; i < slots.length; i++) {
            if (!slots[i].getAttribute('data-adsbygoogle-status')) {
                return true;
            }
        }
        return false;
    }

    /**
     * Initialize an AdSense ad when it becomes visible
     * @param {HTMLElement} adElement - The <ins class="adsbygoogle"> element
     */
    function initAdSenseAdImpl(adElement) {
        if (!adElement) {
            console.warn('AdSense: No ad element provided to queueAdSenseAd()');
            return;
        }

        if (isAlreadyInitialized(adElement) || isQueued(adElement)) {
            return;
        }

        adElement.dataset.adsenseQueued = 'true';

        // Check if IntersectionObserver is supported
        if (!window.IntersectionObserver) {
            // Fallback for older browsers with resize handling
            var checkAndInit = function() {
                if (isElementVisible(adElement)) {
                    pushAdSenseAd(adElement);
                    window.removeEventListener('resize', checkAndInit);
                }
            };

            window.addEventListener('resize', checkAndInit);
            checkAndInit(); // Initial check
            return;
        }

        // Use IntersectionObserver to initialize when visible
        var hasInitialized = false;
        var observer = new IntersectionObserver(function(entries) {
            entries.forEach(function(entry) {
                // Initialize ad when it becomes visible
                if (entry.isIntersecting && !hasInitialized) {
                    hasInitialized = true;
                    pushAdSenseAd(adElement);

                    // Wait for AdSense to process the push() call before disconnecting
                    setTimeout(function() {
                        observer.disconnect();
                        observer = null;
                    }, 100);
                }
            });
        }, {
            threshold: 0.01, // Trigger when at least 1% is visible
            rootMargin: '0px' // Load exactly when entering viewport
        });

        observer.observe(adElement);
    }

    /**
     * Push ad to AdSense queue
     * @param {HTMLElement} adElement
     */
    function pushAdSenseAd(adElement) {
        if (!adElement || isAlreadyInitialized(adElement)) {
            return;
        }

        // A collapsed slot (0 width) makes AdSense throw "No slot size for
        // availableWidth=0". The container width gate in adContent.twig only runs
        // when the slot is inserted; a responsively hidden or since-collapsed slot,
        // or a push fired from a timer/observer, can still reach here with no width.
        // Don't hand AdSense an unsizable slot.
        if (!adElement.offsetWidth) {
            return;
        }

        // push({}) fills the next unprocessed slot anywhere on the page. If all
        // slots are already filled (a redundant trigger, or a raced passback /
        // watchdog push), the call throws "already have ads". Skip it.
        if (!hasUnfilledAdSlot()) {
            return;
        }

        adElement.dataset.adsenseInitialized = 'true';

        try {
            (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch (e) {
            var message = (e && e.message) || '';

            // A collapsed slot or a fully-filled page are expected AdSense
            // conditions that the guards above can still race (async GAM fills,
            // layout shifts). Record a breadcrumb instead of emitting a Sentry
            // event per occurrence.
            if (message.indexOf('availableWidth=0') !== -1 ||
                message.indexOf('already have ads') !== -1) {
                if (window.Sentry && window.Sentry.addBreadcrumb) {
                    window.Sentry.addBreadcrumb({
                        category: 'ads',
                        level: 'warning',
                        message: message,
                        data: {
                            adSlot: adElement.dataset ? adElement.dataset.adSlot : 'unknown'
                        }
                    });
                }
                return;
            }

            console.error('AdSense: Failed to initialize ad', e);

            // Send genuinely unexpected errors to error tracking if available
            if (window.Sentry && window.Sentry.captureException) {
                window.Sentry.captureException(e, {
                    tags: { component: 'adsense' },
                    extra: {
                        adSlot: adElement.dataset ? adElement.dataset.adSlot : 'unknown',
                        visible: safeIsElementVisible(adElement)
                    }
                });
            }
        }
    }

    /**
     * Safe wrapper for visibility check during error handling
     * @param {HTMLElement} element
     * @returns {boolean|string}
     */
    function safeIsElementVisible(element) {
        try {
            return isElementVisible(element);
        } catch (e) {
            return 'error-checking-visibility';
        }
    }

    /**
     * Check if element is visible in viewport
     * @param {HTMLElement} element
     * @returns {boolean}
     */
    function isElementVisible(element) {
        if (!element) return false;

        // Check CSS visibility
        var style = window.getComputedStyle(element);
        if (style.display === 'none' ||
            style.visibility === 'hidden' ||
            style.opacity === '0') {
            return false;
        }

        // Check dimensions
        var rect = element.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return false;

        // Check if in viewport
        return (
            rect.top < window.innerHeight &&
            rect.bottom > 0 &&
            rect.left < window.innerWidth &&
            rect.right > 0
        );
    }

    // Push immediately without waiting for IntersectionObserver. Used for ads
    // already known to be CSS-visible and on a deadline (e.g. AdSense passback
    // injected by adFallback.twig before the 5s collapse timer).
    function initAdSenseAdNow(adElement) {
        if (!adElement || isAlreadyInitialized(adElement)) {
            return;
        }
        pushAdSenseAd(adElement);
    }

    window.queueAdSenseAd = initAdSenseAdImpl;
    window.initAdSenseAdNow = initAdSenseAdNow;

    // Drain anything that inline ad snippets queued before this script ran,
    // then replace the array with a self-draining stub. Any ad snippet that
    // executes later (e.g. injected via ajax) hits this push() and is routed
    // straight through the helper, so nothing can be stranded.
    var pending = window.__pendingAdSenseAds || [];
    window.__pendingAdSenseAds = {
        push: function(el) { initAdSenseAdImpl(el); }
    };
    for (var i = 0; i < pending.length; i++) {
        initAdSenseAdImpl(pending[i]);
    }
})();
